import { useState } from 'react'
import { Button, Grid, IconButton, Typography } from '@material-ui/core'
import { makeStyles } from 'tss-react/mui'
import InfoIcon from '@material-ui/icons/Info'
import { Field, Form, FormikProps } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'

import { useCurrentUser } from '@utils/useCurrentUser'
import { composeValidators, useValidators } from '@utils/validators'
import { registerRoute } from '@src/routes'
import vrStyles from '@src/styles/utils/vrStyles'
import { flatCardStyles } from '@src/styles/module/flatCard'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import { Linq } from '@src/components/Linq'
import ShowOrderConfirmationDialog from './ShowOrderConfirmationDialog'
import { AffiliationType, LinkProfileFormValues } from './AccountAffiliation'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import countriesMap from '@localization/countries.json'
import AfflilationHelperDialog, {
  AffiliationHelperDialogType,
} from '@src/components/AffiliationHelperDialog/AffiliationHelperDialog'
import { checkIfIsPRDorPREorPRPCountry } from '@src/components/AffiliationHelperDialog/utils'
import { useGetCountryDetailsQuery } from '@src/queries/CountryAccountSetupQuery.generated'

const useStyles = makeStyles()((theme) => {
  const { vr2, vr6, vr10 } = vrStyles
  const { flatCard } = flatCardStyles(theme)

  return {
    fieldset: {
      border: 0,
      padding: '0.01em 0 0 0',
      margin: 0,
      minWidth: 0,
    },
    legend: {
      padding: 0,
      display: 'table',
      ...vr6,
      [theme.breakpoints.up('md')]: {
        ...vr10,
      },
    },
    label: {
      display: 'flex',
      alignItems: 'center',
    },
    infoBtn: {
      marginLeft: theme.spacing(1),
      padding: 0,
    },
    submitBtn: {
      [theme.breakpoints.down('lg')]: {
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(6), // "label" spacing allows submit button to stay aligned with fields when 1 or 2 errors are present
      },
    },
    vr2,
    titleCase: {
      textTransform: 'capitalize',
    },
    flatCard,
  }
})

// Customer Numbers are Sold To account numbers
// They are 8 digits in PRE/PRD (Red / legacy ERP) and 10 digits in PRP (Purple / newer ERP)
const CUSTOMER_NUMBER_MAX_LENGTH = 10

// Order Numbers always have 10 digits (SAP sales order)
// Quote Reference Numbers usually have 8 digits but can be less (SAP quote or SFDC reference)
// Invoice Numbers can also be used for account affiliation but that is not available here currently
// It's unclear why this value was set to 11 originally but leaving it because it's not hurting anything
// Original development: WIBF-2487 FE: Account affiliation from Orders page, WIBF-2488 FE: Account affiliation from Quotes page
const QUOTE_REF_OR_ORDER_NUMBER_MAX_LENGTH = 11

interface AccountAffiliationFormProps {
  affiliationType: AffiliationType
  formikBag: FormikProps<LinkProfileFormValues>
}

const AccountAffiliationForm = ({
  affiliationType,
  formikBag,
}: AccountAffiliationFormProps) => {
  const { classes } = useStyles()
  const { formatMessage } = useIntl()
  const { required, hasNoEmojis } = useValidators()

  const { currentUser } = useCurrentUser()
  const isKoreaUser = useCheckUserCountry(countriesMap.KR.countryCode)
  const userCountryCode =
    currentUser?.__typename === 'LoggedInUser'
      ? currentUser?.metadata?.country
      : ''
  const { data: countryData } = useGetCountryDetailsQuery({
    variables: {
      country: userCountryCode,
    },
    fetchPolicy: 'no-cache',
    skip: !currentUser || currentUser.__typename !== 'LoggedInUser',
  })

  const erpSystemId = countryData?.getCountryDetails?.erpSystemId || ''

  const isPRDorPREorPRPCountryUser = checkIfIsPRDorPREorPRPCountry(erpSystemId)

  const [showOrderConfirmedModal, setShowOrderConfirmedModal] = useState(false)
  const [customerIconFlag, setCustomerIconFlag] = useState(false)
  const [
    showAffiliationHelperDialogModal,
    setShowAffiliationHelperDialogModal,
  ] = useState<AffiliationHelperDialogType>(AffiliationHelperDialogType.NONE)
  return (
    <Form className={classes.flatCard}>
      <fieldset className={classes.fieldset}>
        {/* Form Legend */}
        {/* Legend element should be the first child of fieldset: https://www.accessibility-developer-guide.com/examples/forms/grouping-with-fieldset-legend/#legend-must-be-a-direct-child-of-fieldset */}
        <Grid container component="legend" className={classes.legend}>
          <Grid item xs={12} sm={8}>
            <Typography component="h2" variant="h3" className={classes.vr2}>
              <FormattedMessage
                id="ACCOUNT_AFFILIATION_HEADING"
                defaultMessage="Connect for {type}"
                values={{
                  type: (
                    <span className={classes.titleCase}>{affiliationType}</span>
                  ),
                }}
              ></FormattedMessage>
            </Typography>
            <Typography className={classes.vr2}>
              <FormattedMessage
                id="ACCOUNT_AFFILIATION_DESCRIPTION"
                defaultMessage="To search, display, and purchase from {type}, please connect with your organizational information. If you don't have the corresponding information, you may {link}."
                values={{
                  link: (
                    <Linq
                      href={
                        isKoreaUser
                          ? '/life-science/ecommerce/new-account'
                          : `${registerRoute.linkProfile()}`
                      }
                    >
                      <FormattedMessage
                        id="ACCOUNT_AFFILIATION_DESCRIPTION_LINK"
                        defaultMessage="connect with other information or complete registration"
                      />
                    </Linq>
                  ),
                  type: affiliationType,
                }}
              ></FormattedMessage>
            </Typography>
            <Typography variant="body2" component="p">
              *&nbsp;
              <FormattedMessage
                id="REQUIRED_FIELDS"
                defaultMessage="Required Fields"
              />
            </Typography>
          </Grid>
        </Grid>

        {/* Form Fields */}
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Field
              name="customerNumberValue"
              component={LiquidInputAdapter}
              label={
                <span className={classes.label}>
                  <Typography variant="body2" component="span">
                    *&nbsp;
                    <FormattedMessage
                      id="CUSTOMER_NUMBER"
                      defaultMessage="Customer Number"
                    />
                  </Typography>
                  <IconButton
                    aria-label={formatMessage({
                      id: 'ADDITIONAL_INFO_POPOVER',
                      defaultMessage: 'View Additional Information',
                    })}
                    className={classes.infoBtn}
                    onClick={() => {
                      if (isPRDorPREorPRPCountryUser) {
                        setShowAffiliationHelperDialogModal(
                          AffiliationHelperDialogType.CUSTOMER
                        )
                        return
                      }
                      setShowOrderConfirmedModal(true)
                      setCustomerIconFlag(true)
                    }}
                  >
                    <InfoIcon color="primary" />
                  </IconButton>
                </span>
              }
              filled
              validate={composeValidators(required, hasNoEmojis)}
              inputProps={{
                'data-scrub': 'true',
                maxLength: CUSTOMER_NUMBER_MAX_LENGTH,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Field
              name="transactionalInfoValue"
              component={LiquidInputAdapter}
              label={
                <span className={classes.label}>
                  <Typography variant="body2" component="span">
                    *&nbsp;
                    {affiliationType === AffiliationType.Orders && (
                      <FormattedMessage
                        id="ORDER_NUMBER"
                        defaultMessage="Order Number"
                      />
                    )}
                    {affiliationType === AffiliationType.Quotes && (
                      <FormattedMessage
                        id="QUOTES_REF_NUMBER"
                        defaultMessage="Quote Reference Number"
                      />
                    )}
                  </Typography>
                  <IconButton
                    aria-label={formatMessage({
                      id: 'ADDITIONAL_INFO_POPOVER',
                      defaultMessage: 'View Additional Information',
                    })}
                    className={classes.infoBtn}
                    onClick={() => {
                      if (isPRDorPREorPRPCountryUser) {
                        setShowAffiliationHelperDialogModal(
                          AffiliationHelperDialogType.ORDER
                        )
                        return
                      }
                      setShowOrderConfirmedModal(true)
                      setCustomerIconFlag(false)
                    }}
                  >
                    <InfoIcon color="primary" />
                  </IconButton>
                </span>
              }
              filled
              validate={composeValidators(required, hasNoEmojis)}
              inputProps={{
                'data-scrub': 'true',
                maxLength: QUOTE_REF_OR_ORDER_NUMBER_MAX_LENGTH,
              }}
            />
          </Grid>

          {/* Form Submit */}
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              color="primary"
              className={classes.submitBtn}
              disabled={formikBag.isSubmitting}
              id="link-profile-form-submit"
            >
              <FormattedMessage
                id="ACCOUNT_AFFILIATION_CONNECT"
                defaultMessage="Connect"
              />
            </Button>
          </Grid>
        </Grid>

        {/* Confirmation Modal */}
        <ShowOrderConfirmationDialog
          open={showOrderConfirmedModal}
          handleClose={() => {
            setShowOrderConfirmedModal(false)
            setCustomerIconFlag(false)
          }}
          erpSystemId={erpSystemId}
          customerIconFlag={customerIconFlag}
          type={affiliationType}
        />

        {isPRDorPREorPRPCountryUser && (
          <AfflilationHelperDialog
            dialogType={showAffiliationHelperDialogModal}
            erpSystemId={erpSystemId}
            handleClose={() => {
              setShowAffiliationHelperDialogModal(
                AffiliationHelperDialogType.NONE
              )
            }}
          />
        )}
      </fieldset>
    </Form>
  )
}

export default AccountAffiliationForm
